/**
 * An entry's face — href, label and icon — as the viewer sees it (#14279,
 * #15618). The top header's configurable entries lived here until #16727
 * deleted the top navigation; the face is what survives, shared by the side
 * navigation (`app-sidebar.tsx`) and its account menu
 * (`account-menu-entries.tsx`), so a renamed entry reads the same in both.
 *
 * CLIENT-SAFE: no React rendering, no server reads.
 */

import type { ComponentType } from 'react'

import { Activity } from '@/components/icons'
import type { TerminologyContextValue } from '@/contexts/terminology-context'
import { ENTRIES, type EntryKey } from '@/lib/org-nav/vocabulary'
import { HandbookIcon, PlansIcon } from '@/lib/ui-icons'

export interface EntryFace {
  href: string
  label: string
  Icon: ComponentType<{ className?: string }>
}

/**
 * An entry's href, label and icon as the viewer sees them. Terminology-driven
 * faces: an org may rename these, and the nav must announce what the viewer
 * actually sees. Shared with the side navigation (#15618), so a renamed Plans
 * entry reads the same in both navs.
 */
export function navEntryFace(
  itemKey: EntryKey,
  terminology: TerminologyContextValue | null | undefined,
): EntryFace {
  switch (itemKey) {
    case 'pulse':
      return {
        href: '/pulse',
        label: terminology?.getLabel('pulseCheck') ?? 'Pulse',
        Icon: terminology?.getIcon('pulseCheck') ?? Activity,
      }
    case 'orgs':
      return {
        href: '/handbook',
        label: terminology?.getLabel('handbook') ?? 'Handbook',
        Icon: terminology?.getIcon('handbook') ?? HandbookIcon,
      }
    case 'plans':
      return {
        href: '/plans',
        label: terminology?.getLabel('plan', true) ?? 'Plans',
        Icon: terminology?.getIcon('plan') ?? PlansIcon,
      }
    default: {
      const item = ENTRIES[itemKey]
      return {
        href: item.href ?? '/today',
        label: item.label,
        Icon: item.icon,
      }
    }
  }
}
